/**
 * PDF downloads - g.pdf(doc, params, opts)
 *
 * The client side of the PDF document system (server: /frm/pdf/render;
 * the whole mechanism: genus-php/docs/pdf.md). One call generates a
 * DECLARED document and handles BOTH answer shapes:
 *
 *   - the PDF itself → triggers the browser download (opts.download:
 *     false skips it and hands you the blob instead);
 *   - the busy conversion → { queued: true, task } — the server moved
 *     the job to a background task; poll g.fetch('/frm/pdf/task',
 *     { task }) and, when done, link the finished file (its PRIVATE
 *     frm.files row) wherever your UI shows it.
 *
 * Always returns an envelope-shaped object:
 *   { success: true,  data: { downloaded: true, filename, blob } }
 *   { success: true,  data: { queued: true, task } }
 *   { success: true,  error: { code, ... } }        (business refusal)
 *   { success: false, error: { code, message } }    (transport failure)
 * so callers branch exactly like after g.fetch.
 */

import { resolveEndpoint, standardHeaders } from './fetch.js';
import { translate } from './translate.js';
import { progressStart, progressEnd } from './progress.js';

const failure = (code, message) => ({ success: false, error: { code, message } });

export async function pdf(doc, params, opts = {}) {
	const target = resolveEndpoint('/frm/pdf/render');
	if (!target) return failure(1, translate('Invalid endpoint: {endpoint}', { endpoint: '/frm/pdf/render' }));
	const headers = standardHeaders(target);
	headers['Content-Type'] = 'application/json';

	if (opts.progress !== false) progressStart();
	let res;
	try {
		res = await window.fetch(target.url, {
			method: 'POST',
			credentials: 'include',
			headers,
			body: JSON.stringify({ data: { doc, params: params || {} } }),
			signal: opts.signal,
		});

		const type = res.headers.get('content-type') || '';
		if (type.includes('application/pdf')) {
			const blob = await res.blob();
			const filename = dispositionFilename(res.headers.get('content-disposition')) || `${doc}.pdf`;
			if (opts.download !== false) {
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				a.remove();
				setTimeout(() => URL.revokeObjectURL(url), 10000);
			}
			return { success: true, error: { code: 0, message: '' }, data: { downloaded: opts.download !== false, filename, blob } };
		}

		// everything else is the JSON envelope: queued conversion, the data
		// function's refusal/validation, or an API error — pass it through
		try {
			return JSON.parse(await res.text());
		} catch {
			return failure(3, translate('Invalid API response'));
		}
	} catch (err) {
		if (err && err.name === 'AbortError') return failure(4, translate('Request aborted'));
		return failure(2, translate('API not responding'));
	} finally {
		if (opts.progress !== false) progressEnd();
	}
}

// Content-Disposition: prefer the RFC 5987 utf-8 name, fall back to the
// quoted ascii one
function dispositionFilename(header) {
	if (!header) return null;
	const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8) {
		try { return decodeURIComponent(utf8[1]); } catch { /* fall through */ }
	}
	const plain = header.match(/filename="([^"]+)"/i);
	return plain ? plain[1] : null;
}
